import { Box, Skeleton, type SxProps } from "@mui/material";
import airBank from "Assets/Images/Banks/ab.png";
import artesa from "Assets/Images/Banks/artesa.png";
import citfin from "Assets/Images/Banks/citfin.svg";
import cnb from "Assets/Images/Banks/cnb.svg";
import creditas from "Assets/Images/Banks/creditas.svg";
import csas from "Assets/Images/Banks/csas.png";
import csob from "Assets/Images/Banks/csob.png";
import fio from "Assets/Images/Banks/fio.png";
import jandt from "Assets/Images/Banks/jandt.svg";
import kb from "Assets/Images/Banks/kb.png";
import maxbanka from "Assets/Images/Banks/maxbanka.svg";
import mbank from "Assets/Images/Banks/mbank.jpg";
import moneta from "Assets/Images/Banks/moneta.svg";
import ney from "Assets/Images/Banks/ney.svg";
import nrb from "Assets/Images/Banks/nrb.png";
import oberbank from "Assets/Images/Banks/oberbank.svg";
import partners from "Assets/Images/Banks/partners.svg";
import peneznidum from "Assets/Images/Banks/peneznidum.svg";
import ppf from "Assets/Images/Banks/ppf.svg";
import rb from "Assets/Images/Banks/rb.png";
import revolut from "Assets/Images/Banks/revolut.png";
import trinity from "Assets/Images/Banks/trinity.svg";
import unicredit from "Assets/Images/Banks/unicredit.png";
import czkt from "Assets/Images/Banks/czkt.svg";
import type { Bank } from "Infrastructure/Api/Api";

const HEIGHT = 32;
const HEIGHT_SMALL = 24;
const WIDTH = 64;
const WIDTH_SMALL = 48;

type Logo = {
  image: string;
  dimensions: {
    normal: { width?: number; height?: number };
    small: { width?: number; height?: number };
  };
};

const LOGOS: Record<Bank, Logo> = {
  CSOB: {
    image: csob,
    dimensions: {
      normal: { height: HEIGHT },
      small: { height: HEIGHT_SMALL },
    },
  },
  CSAS: {
    image: csas,
    dimensions: {
      normal: { height: HEIGHT },
      small: { height: HEIGHT_SMALL },
    },
  },
  KB: {
    image: kb,
    dimensions: {
      normal: { height: HEIGHT },
      small: { height: HEIGHT_SMALL },
    },
  },
  AirBank: {
    image: airBank,
    dimensions: {
      normal: { height: HEIGHT },
      small: { height: HEIGHT_SMALL },
    },
  },
  UCB: {
    image: unicredit,
    dimensions: {
      normal: { width: WIDTH },
      small: { width: WIDTH_SMALL },
    },
  },
  MONETA: {
    image: moneta,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  FIO: {
    image: fio,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  MBank: {
    image: mbank,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  RB: {
    image: rb,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  Citfin: {
    image: citfin,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  TRINITY: {
    image: trinity,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  PenezniDum: {
    image: peneznidum,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  Artesa: {
    image: artesa,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  CREDITAS: {
    image: creditas,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  NEY: {
    image: ney,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  MaxBanka: {
    image: maxbanka,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  NRB: {
    image: nrb,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  JandT: {
    image: jandt,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  PPF: {
    image: ppf,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  Oberbank: {
    image: oberbank,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  PartnersBank: {
    image: partners,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  CNB: {
    image: cnb,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  Revolut: {
    image: revolut,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
  CZKT: {
    image: czkt,
    dimensions: {
      normal: { height: HEIGHT, width: WIDTH },
      small: { height: HEIGHT_SMALL, width: WIDTH_SMALL },
    },
  },
};

const getImageUrl = (bank: Bank) =>
  new URL(LOGOS[bank].image, import.meta.url).href;

type Props = {
  bank?: Bank;
  name?: string;
  isLoading?: boolean;
  isRounded?: boolean;
  size?: "normal" | "small";
  isGrayscale?: boolean;
  sx?: SxProps;
  heightOverride?: number | "auto";
  widthOverride?: number | "auto";
};

export const BankImage = ({
  bank,
  name,
  isLoading,
  isRounded,
  size = "normal",
  isGrayscale,
  sx,
  heightOverride,
  widthOverride,
}: Props) => {
  if (isLoading) {
    return (
      <Skeleton
        variant={isRounded ? "rounded" : "rectangular"}
        width={120}
        height={size === "small" ? HEIGHT_SMALL : HEIGHT}
      />
    );
  }

  if (!bank) {
    return null;
  }

  const logo = LOGOS[bank];
  const dimensions = logo.dimensions[size];

  return (
    <Box
      component="img"
      sx={{
        filter: isGrayscale ? "grayscale(100%)" : "none",
        objectFit: "contain",
        ...sx,
      }}
      src={getImageUrl(bank)}
      height={heightOverride || dimensions.height}
      width={widthOverride || dimensions.width}
      alt={`${name || "bank"} logo`}
      style={{ borderRadius: isRounded ? "24px" : "0" }}
    />
  );
};
